import { lazy, useMemo } from 'react';
import { ClipboardCheck, ClipboardPlus, AlarmClock } from 'lucide-react';
import { formatLocalDate, type Task } from '@app/shared';
import type { AttentionItem, WebModule } from '../../app/types.ts';
import { useFamily } from '../../platform/session.tsx';
import { familyRecords, scoreText } from '../../platform/search.ts';
import { daysUntil, dueLabel } from '../../platform/time.ts';
import { taskState, useCompletions, useTasks } from './data.ts';

function useChoreAttention(): AttentionItem[] {
  const tasks = useTasks();
  const completions = useCompletions();
  const { actor } = useFamily();
  return useMemo(() => {
    const today = formatLocalDate(new Date());
    return (tasks ?? [])
      .filter((t) => !t.archived && t.assigneeId === actor.userId)
      .map((t) => taskState(t, completions ?? [], today))
      .filter((s) => s.overdue && !s.doneToday)
      .map((s) => ({
        key: `task:${s.task.id}`,
        title: s.task.title,
        detail: `Chore · ${dueLabel(s.dueDate!)}`,
        to: `/tasks?task=${s.task.id}`,
        tone: 'danger' as const,
        icon: AlarmClock,
        days: daysUntil(s.dueDate!),
      }));
  }, [tasks, completions, actor.userId]);
}

export const tasksModule: WebModule = {
  id: 'tasks',
  label: 'Chores',
  icon: ClipboardCheck,
  path: '/tasks',
  nav: { group: 'main', order: 30, bottomTab: true },
  routes: [{ path: 'tasks', lazy: async () => ({ Component: (await import('./TasksScreen.tsx')).TasksScreen }) }],
  home: [{ id: 'chores', title: 'Chores today', order: 30, component: lazy(() => import('./home.tsx').then((m) => ({ default: m.ChoresSection }))) }],
  quickActions: [{ id: 'chore', label: 'Chore', icon: ClipboardPlus, to: '/tasks?add=1', adultOnly: true }],
  useAttention: useChoreAttention,
  async search(q, ctx) {
    const tasks = await familyRecords<Task>('tasks', ctx.familyId);
    return tasks
      .map((t) => ({ t, score: scoreText(q, [t.title, t.description]) }))
      .filter((x) => x.score > 0)
      .map(({ t, score }) => ({
        key: `tasks:${t.id}`,
        title: t.title,
        subtitle: `${t.kind === 'todo' ? 'To-do' : 'Chore'}${t.assigneeId ? ` · ${ctx.nameOf(t.assigneeId)}` : ''}`,
        to: `/tasks?task=${t.id}`,
        group: 'Chores',
        icon: ClipboardCheck,
        score,
      }));
  },
};
